import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Alert } from '@thegpvc/ui';
import { api } from '../api/client';
import { LeverageCard } from './LeverageCard';
import { DailyLeverageChart } from './DailyLeverageChart';
import { SpendCard } from './SpendCard';
import { OverheadCard } from './OverheadCard';
import { errorMessage } from '../utils/errorMessage';
import { cents } from '../utils/cents';
import type { TimePeriod } from '../types/api';

/**
 * UsageView is the Stats ▸ Usage tab (#630) — the leverage story for a range,
 * composed from ONE range-keyed payload so every figure (driving hours, agent
 * hours, leverage, spend, breakdowns, overhead, sessions) re-derives atomically
 * when the range pill changes. Four cards: the leverage equation, the daily
 * leverage chart, spend by model/project, and the overhead ledger.
 */
export function UsageView({ period }: { period: TimePeriod }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['usage', period],
    queryFn: () => api.getUsage(period),
    placeholderData: keepPreviousData,
  });

  if (error) {
    return <Alert variant="error">Failed to load usage: {errorMessage(error)}</Alert>;
  }

  if (isLoading && !data) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-40 animate-pulse rounded-lg bg-gray-100 dark:bg-navy-800" />
        ))}
      </div>
    );
  }

  const days = data?.days ?? [];
  const spend = cents(data?.spendCents);

  // Empty range: nothing to show as real numbers, so render a message rather
  // than zeroed cards (§9 — no field survives an empty swap as a fake zero).
  if (!data || (days.length === 0 && spend === 0)) {
    return (
      <Alert variant="info">No usage recorded in this period yet.</Alert>
    );
  }

  return (
    <div className="space-y-6">
      <LeverageCard data={data} />
      {days.length > 0 && <DailyLeverageChart days={days} range={period} />}
      <SpendCard data={data} />
      <OverheadCard data={data} />
    </div>
  );
}
